import {
  BurnId,
  BurnTemperatureSeriesNonSensitive,
  BurnTemperatureSeriesNonSensitiveImpl,
  flueBurnMetricAggregateTimeUnits,
} from "firefly-shared-ts-domain";
import React, { FC, useMemo } from "react";
import {
  DurationSeries,
  DurationSeriesImpl,
} from "firefly-shared-ts-timeseries";
import { alpha, SxProps, useTheme } from "@mui/material";
import { Flue } from "firefly-my-chimney-ts-building";
import {
  ChartsReferenceLineProps,
  ChartsXReferenceLineProps,
  XAxis,
  YAxis,
} from "@mui/x-charts-premium";
import { useResolveMaxTemperatureReferenceLineProps } from "./useResolveMaxTemperatureReferenceLineProps";
import { DateTime, Duration } from "luxon";
import { DurationSeriesMuiChart } from "firefly-shared-ts-durationseries-mui-chart";
import { MuiStructuralChartContainer } from "shared-ts-mui";
import { SizeClass } from "shared-ts-ui-responsive";
import { interpolateRdYlBu } from "d3-scale-chromatic";
import { useResolveAvgMaxTemperatureReferenceLineProps } from "./useResolveAvgMaxTemperatureReferenceLineProps";

const unit = "°C";

export interface BurnTimeSeriesAnonymizedChartProps {
  flue: Flue;
  burnTimeSeries?: Array<BurnTemperatureSeriesNonSensitive>;
  selected: Array<BurnId>;
  selectedMonth?: DateTime;
  timeAxis?: {
    maxDuration?: Duration | undefined;
  };
  hideLegend?: boolean;
  hideMaxTemperatureReferenceLines?: boolean;
  hideAvgMaxTemperatureReferenceLines?: boolean;
  layout?: SizeClass;
  sx?: SxProps;
}

export const BurnTimeSeriesAnonymizedChart: FC<
  BurnTimeSeriesAnonymizedChartProps
> = (props) => {
  const { flue, burnTimeSeries, selected, selectedMonth } = props;
  const hideMaxTemperatureReferenceLines =
    props.hideMaxTemperatureReferenceLines ?? false;
  const hideAvgMaxTemperatureReferenceLines =
    props.hideAvgMaxTemperatureReferenceLines ?? false;
  const theme = useTheme();
  const flueMetricsSinceStart = flue.querySingleMetricsOrUndef(
    flueBurnMetricAggregateTimeUnits.ever
  );

  const maxTemperatureSinceStart = flueMetricsSinceStart?.values.temperatureMax;

  const maxY = useMemo(() => {
    return maxTemperatureSinceStart ?? 100;
  }, [maxTemperatureSinceStart]);

  const maxTemperatureReferenceLineProps =
    useResolveMaxTemperatureReferenceLineProps(flue, unit, selectedMonth);

  const avgMaxTemperatureReferenceLineProps =
    useResolveAvgMaxTemperatureReferenceLineProps(flue, unit, selectedMonth);

  const timeSeriesArray: Array<DurationSeries<number | null>> | undefined =
    useMemo(() => {
      return burnTimeSeries
        ?.filter((it) => selected.includes(it.burnId))
        .map((burnSeries) => {
          return new DurationSeriesImpl({
            info: {
              id: `${burnSeries.burnId}`,
              sid: flue.id,
              name: "",
              resolution: "",
              unit: unit,
            },
            points: burnSeries.dataPoints,
          });
        });
    }, [burnTimeSeries, selected, flue.id]);

  const yAxisConfig: Array<YAxis<"linear">> = useMemo(() => {
    const minY =
      burnTimeSeries !== undefined
        ? BurnTemperatureSeriesNonSensitiveImpl.getMinY(burnTimeSeries, 0)
        : 0;

    return [
      {
        max: maxY,
        min: minY,
        colorMap: {
          type: "continuous",
          min: minY,
          max: maxY,
          color: (value) => interpolateRdYlBu(1 - (0.5 + 0.5 * value)),
        },
      } as YAxis<"linear">,
    ];
  }, [burnTimeSeries, maxY]);

  const xAxisMax = useMemo(() => {
    if (
      timeSeriesArray == null ||
      timeSeriesArray.length === 0 ||
      props.timeAxis?.maxDuration === undefined
    ) {
      return undefined;
    }

    if (timeSeriesArray.length > 1) {
      throw new Error("Viewing more than one time series is not supported yet");
    }

    return timeSeriesArray[0].firstPoint?.duration.plus(
      props.timeAxis?.maxDuration
    );
  }, [props.timeAxis?.maxDuration, timeSeriesArray]);

  const ignitionEndTime = useMemo(() => {
    if (timeSeriesArray == null || timeSeriesArray.length === 0) {
      return undefined;
    }

    if (timeSeriesArray.length > 1) {
      throw new Error("Viewing more than one time series is not supported yet");
    }

    return timeSeriesArray[0].firstPoint?.duration.plus({ minutes: 15 });
  }, [timeSeriesArray]);

  const xAxisConfig: XAxis = useMemo(() => {
    return {
      max: xAxisMax?.toMillis(),
      tickInterval: tickInterval,
    } as XAxis<"linear">;
  }, [xAxisMax]);

  const referenceLines = useMemo(() => {
    let referenceLines: Array<ChartsReferenceLineProps> = [];
    if (ignitionEndTime !== undefined) {
      referenceLines.push({
        x: ignitionEndTime.toMillis(),
        lineStyle: {
          stroke: alpha(theme.palette.divider, 0.2),
        },
      } as ChartsXReferenceLineProps);
    }
    if (!hideMaxTemperatureReferenceLines) {
      referenceLines = referenceLines.concat(maxTemperatureReferenceLineProps);
    }
    if (!hideAvgMaxTemperatureReferenceLines) {
      referenceLines = referenceLines.concat(
        avgMaxTemperatureReferenceLineProps
      );
    }
    return referenceLines;
  }, [
    avgMaxTemperatureReferenceLineProps,
    hideAvgMaxTemperatureReferenceLines,
    hideMaxTemperatureReferenceLines,
    ignitionEndTime,
    maxTemperatureReferenceLineProps,
    theme.palette.divider,
  ]);

  return (
    <MuiStructuralChartContainer sx={props.sx}>
      <DurationSeriesMuiChart
        durationSeries={timeSeriesArray ?? []}
        yAxisConfig={yAxisConfig}
        xAxisConfig={xAxisConfig}
        referenceLines={referenceLines}
        hideLegend={props.hideLegend}
      />
    </MuiStructuralChartContainer>
  );
};

const tickInterval = (duration: Duration): boolean => {
  if (duration <= Duration.fromDurationLike({ hours: 1 })) {
    return duration.as("minutes") % 15 === 0;
  }
  return duration.as("minutes") % 60 === 0;
};
